/**
 * Groups Page - Översikt över användarens beredskapsgrupper
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@/stores/auth-store';
import { apiPost, apiGet, getUnreadCounts } from '@/services/api-client';
import resizeFileToBase64 from '@/utils/image';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  ChevronRight,
  Crown,
  X,
  MapPin,
  Search,
  Clock,
} from 'lucide-react';
import { Globe, Lock } from 'lucide-react';
import { useAppModeStore } from '@/stores/app-mode-store';
import { CityAutocomplete } from '@/components/CityAutocomplete';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';

// Simple Create Group modal
function CreateGroupModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: { name: string; description?: string; isPublic: boolean; city?: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [city, setCity] = useState('');
  // invites removed from create flow
  const [creating, setCreating] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageResizedBase64, setImageResizedBase64] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    lockScroll();
    return () => {
      unlockScroll();
    };
  }, [open]);

  if (!open) return null;

  const modal = (
    <div
      className="fixed z-[9999] flex items-center justify-center p-4 overflow-y-auto"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) * -1 - 24px)',
        left: 0,
        right: 0,
        bottom: 0,
        margin: 0,
        minHeight: 'calc(100dvh + env(safe-area-inset-top, 0px) + 24px)',
      }}
    >
      <div
        className="absolute inset-0 bg-black/40"
        style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-lg bg-card p-6 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('groups.create')}</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-5">
          <div>
            <label className="block text-sm">Namn</label>
            <input 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              maxLength={40} 
              className="w-full mt-1 rounded-lg border border-border p-2 bg-background" 
            />
            <div className="text-sm text-muted-foreground mt-1">{name.length}/40</div>
          </div>
          <div>
            <label className="block text-sm">Beskrivning</label>
            <textarea 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              maxLength={250}
              className="w-full mt-1 rounded-lg border border-border p-2 bg-background" 
            />
            <div className="text-sm text-muted-foreground mt-1">{description.length}/250 tecken</div>
          </div>

          <div>
            <label className="block text-sm">Ort (valfritt)</label>
            <CityAutocomplete
              value={city}
              onChange={setCity}
              placeholder="Välj ort..."
              className="w-full mt-1 rounded-lg border border-border p-2 bg-background"
              maxLength={20}
            />
            {city && <div className="text-sm text-muted-foreground mt-1">{city.length}/20</div>}
          </div>

          <div>
            <label className="block text-sm">Gruppbild</label>
            <div className="flex items-center gap-3 mt-2">
              <div className="w-20 h-20 rounded-md overflow-hidden bg-muted/10 flex items-center justify-center border border-orange-500/60">
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Users className="h-6 w-6 text-orange-500" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  accept="image/*"
                  id="create-group-image-upload"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) {
                      setImageFile(null);
                      setPreviewUrl(null);
                      setImageResizedBase64(null);
                      return;
                    }
                    setImageFile(f);
                    // Resize immediately and show resized preview so user sees exactly what's uploaded
                    (async () => {
                      try {
                        const base64 = await resizeFileToBase64(f, 512, 0.8);
                        setImageResizedBase64(base64);
                        setPreviewUrl(`data:image/jpeg;base64,${base64}`);
                      } catch {
                        // Fallback to object URL if resize fails
                        const url = URL.createObjectURL(f);
                        setPreviewUrl(url);
                        setImageResizedBase64(null);
                      }
                    })();
                  }}
                />
                <div className="flex gap-2">
                  <label htmlFor="create-group-image-upload" className="px-3 py-1 rounded border border-border cursor-pointer hover:bg-muted inline-block">Välj bild</label>
                  {previewUrl && (
                    <button type="button" onClick={() => { setImageFile(null); setPreviewUrl(null); setImageResizedBase64(null); }} className="px-3 py-1 rounded border border-border text-muted-foreground hover:bg-muted">Ta bort bild</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
              <span>Publik (sökbar för andra användare)</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={onClose} className="rounded-lg px-3 py-2 border border-border">Avbryt</button>
            <button
              onClick={async () => {
                  if (!name.trim()) return;
                  if (name.length > 40) {
                    alert('Namnet får vara max 40 tecken');
                    return;
                  }
                  setCreating(true);
                  try {
                    // If an image was provided, resize client-side and include in payload
                    if (imageFile) {
                      const data = imageResizedBase64 ?? await resizeFileToBase64(imageFile, 512, 0.8);
                      await onCreate({ name, description, isPublic, city: city || undefined, image: { filename: imageFile.name, contentType: 'image/jpeg', data } } as any);
                      // Clear resized base64 after use
                      setImageResizedBase64(null);
                    } else {
                      await onCreate({ name, description, isPublic, city: city || undefined });
                    }
                  } finally {
                    setCreating(false);
                  }
                }}
                disabled={!name.trim() || creating || name.length > 40}
              className="rounded-lg px-3 py-2 btn-primary-custom disabled:opacity-50"
            >
              {creating ? 'Skapar...' : 'Skapa'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }

  return modal;
}

// Placeholder data - kommer från API senare

export default function GroupsPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [searchQuery, _setSearchQuery] = useState('');
  // Local groups state for demo creation (start empty)
  interface Group {
    id: string;
    name: string;
    description?: string;
    memberCount?: number;
    role?: 'owner' | 'member';
    lastActivity?: Date;
    unreadMessages?: number;
    pendingRequestCount?: number;
    isPublic?: boolean;
    invites?: string[];
    imageUrl?: string;
    city?: string;
  }
  const [groups, setGroups] = useState<Group[]>(() => []);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, { unreadThreads: number; newBoardNotes: number; newCalendarEvents: number; newWikiArticles: number }>>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const { setMode: setAppMode } = useAppModeStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  // Check URL parameter to open create modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === 'true') {
      setShowCreateModal(true);
      // Clean up URL
      navigate('/app/groups', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        // Invalidate unread counts to update badges in Layout
        queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
        
        const res = await apiGet<Group[]>('/groups');
        if (!mounted) return;
        if (res.success && res.data) {
          const groupsWithCounts = await Promise.all(res.data.map(async (g) => {
            const gg = g as unknown as Record<string, unknown>;
            const ownerId = typeof gg.ownerId === 'string' ? gg.ownerId : undefined;
            const members = Array.isArray(gg.members) ? gg.members : undefined;
            const lastActivityAt = typeof gg.lastActivityAt === 'string' ? gg.lastActivityAt : (typeof gg.updatedAt === 'string' ? gg.updatedAt : undefined);
            const image = gg.image && typeof gg.image === 'object' ? gg.image as Record<string, unknown> : undefined;
            const groupId = String(gg.id || '');
            const currentUserId = useAuthStore.getState().user?.id;
            
            // Determine role: check if original owner OR has owner role in members array
            let userRole: 'owner' | 'member' = 'member';
            const isOriginalOwner = ownerId && currentUserId && ownerId === currentUserId;
            if (isOriginalOwner) {
              userRole = 'owner';
            } else if (members && currentUserId) {
              const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
              if (userMember && userMember.role === 'owner') {
                userRole = 'owner';
              }
            }
            
            // Fetch pending request count if owner
            let pendingRequestCount = 0;
            if (userRole === 'owner' && groupId) {
              try {
                const reqRes = await apiGet<{ requests: unknown[] }>(`/groups/${groupId}/membership-requests`);
                if (reqRes.success && reqRes.data && reqRes.data.requests) {
                  pendingRequestCount = reqRes.data.requests.length;
                }
              } catch (e) {
                // Silent fail - not critical
              }
            }
            
            const createdGroup = {
              id: groupId,
              name: String(gg.name || ''),
              description: typeof gg.description === 'string' ? (gg.description as string) : undefined,
              memberCount: typeof gg.memberCount === 'number' ? gg.memberCount : (members ? members.length : 1),
              role: userRole,
              lastActivity: lastActivityAt ? new Date(lastActivityAt) : undefined,
              unreadMessages: 0,
              pendingRequestCount,
              isPublic: typeof gg.isPublic === 'boolean' ? gg.isPublic : undefined,
              imageUrl: image && typeof image.url === 'string' ? image.url : undefined,
              city: typeof gg.city === 'string' ? gg.city : undefined,
            };
            return createdGroup;
          }));
          
          if (mounted) {
            setGroups(groupsWithCounts);
          }
        }
      } catch (e) {
        console.error('Failed to load groups', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fetch unread counts for all groups
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getUnreadCounts();
        if (mounted && res.success && res.data) {
          setUnreadCounts(res.data);
        }
      } catch (e) {
        console.error('Failed to load unread counts', e);
      }
    })();
    return () => { mounted = false; };
  }, [groups]); // Re-fetch when groups change

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    // Sort by lastActivity (most recent first)
    const aTime = a.lastActivity?.getTime() || 0;
    const bTime = b.lastActivity?.getTime() || 0;
    return bTime - aTime;
  });

  // Create function - calls backend API
  const createGroup = async (payload: {
    name: string;
    description?: string;
    isPublic: boolean;
    city?: string;
    image?: { filename?: string; contentType?: string; data?: string } | null;
  }) => {
    // Optimistic UI: disable modal while creating
    try {
      const res = await apiPost<{ id: string; name: string; description?: string; memberCount?: number; members?: unknown[]; updatedAt?: string; isPublic?: boolean }>('/groups', payload);
      if (!res.success) {
        // Basic error handling - show alert for now
        alert(res.error || 'Failed to create group');
        return;
      }

      const created = res.data as { id: string; name: string; description?: string; memberCount?: number; members?: unknown[]; updatedAt?: string; isPublic?: boolean };
      // Normalize and add to local list
      const newGroup = {
        id: created.id,
        name: created.name,
        description: created.description,
        memberCount: created.memberCount || (created.members ? created.members.length : 1),
        role: 'owner' as const,
        lastActivity: new Date(created.updatedAt || Date.now()),
        unreadMessages: 0,
        isPublic: created.isPublic,
        invites: [],
        imageUrl: undefined as string | undefined,
      };

      // If caller included image data, upload it to image endpoint
      if ((payload as any).image && (payload as any).image.data) {
        try {
          const img = (payload as any).image;
          const uploadRes = await apiPost<{ url: string }>(`/groups/${created.id}/image`, img);
          if (uploadRes.success && uploadRes.data && (uploadRes.data as any).url) {
            newGroup.imageUrl = (uploadRes.data as any).url;
          }
        } catch (e) {
          console.error('Failed to upload group image', e);
        }
      }

      setGroups((s) => [newGroup, ...s]);
      queryClient.setQueryData<Array<{ id: string; name: string; imageUrl?: string; unreadMessages?: number; pendingRequestCount?: number }>>(
        ['groups-badge-count', user?.id],
        (current) => {
          const existing = current || [];
          if (existing.some((group) => group.id === newGroup.id)) {
            return existing;
          }

          return [
            {
              id: newGroup.id,
              name: newGroup.name,
              imageUrl: newGroup.imageUrl,
              unreadMessages: 0,
              pendingRequestCount: 0,
              lastActivityAt: created.updatedAt || new Date().toISOString(),
            },
            ...existing,
          ];
        }
      );
      void queryClient.invalidateQueries({ queryKey: ['groups-badge-count', user?.id] });
      void queryClient.invalidateQueries({ queryKey: ['groups-unread-counts', user?.id] });
      setShowCreateModal(false);
    } catch (e) {
      console.error('Create group failed', e);
      alert('Misslyckades skapa grupp');
    }
  };

  const formatLastActivity = (date?: Date) => {
    const now = new Date();
    if (!date) return t('groups.activity.unknown');
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins === 0) return 'Nyss';
    if (diffMins < 60) return t('groups.activity.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('groups.activity.hoursAgo', { count: diffHours });
    return t('groups.activity.daysAgo', { count: diffDays });
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3">
            <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary shrink-0" />
            <span className="truncate">{t('nav.groupMode')}</span>
          </h1>
        </div>
        </div>

      {/* Subheading under Gruppberedskap (muted paragraph like ShoppingListPage) */}
      <div className="pt-3 pb-2 sm:pb-3">
        <p className="text-muted-foreground">{t('groups.subtitle', 'Samla och samarbeta i dina beredskapsgrupper')}</p>
      </div>

      {/* Create modal trigger (moved below groups list) */}

      {/* Create modal */}
      <CreateGroupModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={(payload) => createGroup(payload)}
      />

      {/* Premium modal */}
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="Skapa beredskapsgrupper"
      />

      {/* Action buttons - above groups list */}
      <div className="space-y-3">
        <Link
          to="/app/groups/search"
          className="flex items-center justify-between w-full p-4 rounded-xl border transition-all duration-200 hover:shadow-2xl hover:-translate-y-0.5 hover:scale-[1.01]"
          style={{ backgroundColor: '#7a57a326', borderColor: '#b48cff99', boxShadow: '0 0 0 1px #b48cff33, 0 8px 24px #b48cff29' }}
        >
          <div className="flex items-center gap-3">
            <Search className="h-6 w-6" style={{ color: '#d6b8ff' }} />
            <p className="font-semibold" style={{ color: '#e9d6ff' }}>
              {t('groups.search.cta', 'Sök beredskapsgrupper')}
            </p>
          </div>
          <ChevronRight className="h-5 w-5" style={{ color: '#d6b8ff' }} />
        </Link>

        <button
          onClick={() => {
            if (user?.subscriptionTier !== 'premium') {
              setShowPremiumModal(true);
              return;
            }
            setShowCreateModal(true);
          }}
          className="flex items-center justify-between w-full p-4 rounded-xl border transition-all duration-200 hover:shadow-2xl hover:-translate-y-0.5 hover:scale-[1.01]"
          style={{ backgroundColor: '#8abf4f24', borderColor: '#b8f07a99', boxShadow: '0 0 0 1px #b8f07a33, 0 8px 24px #b8f07a24' }}
        >
          <div className="flex items-center gap-3">
            <Plus className="h-6 w-6" style={{ color: '#d5f7a8' }} />
            <p className="font-semibold" style={{ color: '#e9ffd0' }}>
              {t('groups.create')}
            </p>
          </div>
          <ChevronRight className="h-5 w-5" style={{ color: '#d5f7a8' }} />
        </button>
      </div>

      {/* Subheading + Groups list */}
      <div className="pt-4 sm:pt-5">
        <h2 className="text-xl sm:text-2xl font-semibold">{t('groups.title')}</h2>
      </div>
      
      {/* Loading skeleton */}
      {loading ? (
        <div className="space-y-3 sm:space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-card p-3 sm:p-4 animate-pulse"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                {/* Avatar skeleton */}
                <div className="rounded-xl bg-muted w-[60px] h-[60px] sm:w-[72px] sm:h-[72px] shrink-0" />
                
                <div className="flex-1 min-w-0 space-y-3">
                  {/* Title skeleton */}
                  <div className="h-5 bg-muted rounded w-2/3" />
                  {/* Description skeleton */}
                  <div className="h-4 bg-muted rounded w-full" />
                  {/* Stats skeleton */}
                  <div className="flex gap-4">
                    <div className="h-4 bg-muted rounded w-20" />
                    <div className="h-4 bg-muted rounded w-24" />
                  </div>
                </div>
                
                {/* Arrow skeleton */}
                <div className="w-5 h-5 bg-muted rounded shrink-0" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredGroups.length > 0 ? (
        <div className="space-y-3 sm:space-y-4">
          {filteredGroups.map((group) => (
            <Link
              key={group.id}
              to={`/groups/${group.id}`}
              onClick={() => setAppMode('group')}
              className="block rounded-xl border-2 border-border/80 bg-card p-3 sm:p-4 transition-all hover:border-primary hover:shadow-lg hover:scale-[1.01]"
              title={`${t('common.open')} ${group.name}`}
              aria-label={`${t('common.open')} ${group.name}`}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                {/* Group avatar */}
                <div className="rounded-xl bg-orange-500/10 border border-orange-500/60 shrink-0 overflow-hidden h-14 w-14 sm:h-16 sm:w-16 flex items-center justify-center">
                  { group.imageUrl ? (
                    <img src={group.imageUrl} alt="Group image" className="h-full w-full object-cover" />
                  ) : (
                    <Users className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Group name */}
                  <h3 className="font-semibold text-base sm:text-lg soft-wrap">{group.name}</h3>
                  
                  {/* Role and visibility badges on separate row */}
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1">
                    {typeof group.isPublic !== 'undefined' && (
                      group.isPublic ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-sm shrink-0" style={{ backgroundColor: '#4c5c41', borderColor: '#4c5c41', color: '#c9e6b5' }}>
                          <Globe className="h-3 w-3" />
                          <span>{t('groups.visibility.public')}</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-sm shrink-0" style={{ backgroundColor: '#66271f', borderColor: '#66271f', color: '#d6a9a3' }}>
                          <Lock className="h-3 w-3" />
                          <span>{t('groups.visibility.private')}</span>
                        </span>
                      )
                    )}
                    {group.role === 'owner' && (
                      <span className="flex items-center gap-2 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-600 dark:bg-yellow-500/30 dark:text-yellow-200 text-sm shrink-0">
                        <Crown className="h-3 w-3" />
                        <span>{t('groups.roles.owner')}</span>
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm sm:text-base text-muted-foreground mt-1 line-clamp-1 soft-wrap">
                    {group.description}
                  </p>

                  {/* Stats */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 sm:mt-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {t('groups.memberCount', { count: group.memberCount })}
                    </span>
                    {group.city && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        <span className="flex items-center gap-1 min-w-0">
                          <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          <span className="soft-wrap">{group.city}</span>
                        </span>
                      </>
                    )}
                    <span className="hidden sm:inline">•</span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {formatLastActivity(group.lastActivity)}
                    </span>
                  </div>
                </div>

                {/* Unread badge & arrow */}
                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  {(() => {
                    const counts = unreadCounts[group.id];
                    const totalBadges = (counts?.unreadThreads || 0) + (counts?.newBoardNotes || 0) + (counts?.newCalendarEvents || 0) + (counts?.newWikiArticles || 0) + (group.pendingRequestCount || 0);
                    return totalBadges > 0 ? (
                      <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white text-xs font-medium shadow-lg" style={{ backgroundColor: '#945123' }}>
                        {totalBadges}
                      </span>
                    ) : null;
                  })()}
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-semibold mb-1">{t('groups.empty.title')}</h3>
          <p className="text-muted-foreground text-sm mb-4">
            {t('groups.empty.description')}
          </p>
          {/* Create button intentionally omitted here; main create button is above the info box */}
        </div>
      )}
      {/* Info box removed: Om gruppberedskap */}

        {/* Quick actions removed for now (discussion/wiki/calendar) */}
    </div>
  );
}
